import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRIORITY_LABELS } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wheat, Plus, Pencil, Trash2, Bug, Warehouse, Gauge } from "lucide-react";

const LEVEL_OPTIONS = [["medio", "Médio"], ["alto", "Alto"], ["critico", "Crítico"]];

function SpeciesDialog({ dialog, setDialog, onSave }) {
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
      <DialogContent data-testid="pga-species-dialog">
        <DialogHeader><DialogTitle className="font-heading">{dialog?.id ? "Editar Espécie PGA" : "Nova Espécie de Praga de Grãos"}</DialogTitle></DialogHeader>
        {dialog && (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Nome popular</Label>
              <Input value={dialog.name} placeholder="Ex.: Caruncho do milho"
                onChange={(e) => setDialog({ ...dialog, name: e.target.value })} data-testid="pga-species-name-input" /></div>
            <div className="space-y-1.5"><Label>Nome científico (opcional)</Label>
              <Input value={dialog.scientific_name} placeholder="Ex.: Sitophilus zeamais"
                onChange={(e) => setDialog({ ...dialog, scientific_name: e.target.value })} data-testid="pga-species-scientific-input" /></div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1.5"><Label>Peso no índice (0.1 a 5)</Label>
                <Input type="number" step="0.1" min="0.1" max="5" value={dialog.weight}
                  onChange={(e) => setDialog({ ...dialog, weight: e.target.value })} data-testid="pga-species-weight-input" /></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                <Switch checked={!!dialog.requires_detail} onCheckedChange={(v) => setDialog({ ...dialog, requires_detail: v })} data-testid="pga-species-detail-switch" />
                Exige detalhe (ex.: "Outros")
              </label>
            </div>
            <Button className="w-full" data-testid="pga-species-save-btn" onClick={onSave}>Salvar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TypeDialog({ dialog, setDialog, onSave }) {
  const setItem = (i, patch) => setDialog({ ...dialog, checklist: dialog.checklist.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="pga-type-dialog">
        <DialogHeader><DialogTitle className="font-heading">{dialog?.id ? "Editar Tipo de Estrutura" : "Novo Tipo de Estrutura"}</DialogTitle></DialogHeader>
        {dialog && (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Nome</Label>
              <Input value={dialog.name} placeholder="Ex.: Silo metálico"
                onChange={(e) => setDialog({ ...dialog, name: e.target.value })} data-testid="pga-type-name-input" /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Checklist do monitoramento</Label>
                <button className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="pga-type-add-item-btn"
                  onClick={() => setDialog({ ...dialog, checklist: [...dialog.checklist, { key: "", label: "", severity: "media" }] })}>
                  <Plus size={12} /> Adicionar item
                </button>
              </div>
              {dialog.checklist.map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`pga-type-item-${i}`}>
                  <Input value={c.label} placeholder="Descrição do item" className="h-9 flex-1"
                    onChange={(e) => setItem(i, { label: e.target.value })} />
                  <Select value={c.severity} onValueChange={(v) => setItem(i, { severity: v })}>
                    <SelectTrigger className="h-9 w-28 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0 cursor-pointer" title="Resposta obrigatória">
                    <input type="checkbox" checked={c.required !== false}
                      onChange={(e) => setItem(i, { required: e.target.checked })} className="h-3.5 w-3.5" />
                    Obrig.
                  </label>
                  <label className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0 cursor-pointer" title="Foto obrigatória">
                    <input type="checkbox" checked={c.photo_required === true}
                      onChange={(e) => setItem(i, { photo_required: e.target.checked })} className="h-3.5 w-3.5" />
                    📸
                  </label>
                  <button className="text-muted-foreground hover:text-destructive"
                    onClick={() => setDialog({ ...dialog, checklist: dialog.checklist.filter((_, j) => j !== i) })}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <Button className="w-full" data-testid="pga-type-save-btn" onClick={onSave}>Salvar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PgaParamsCard() {
  const [pga, setPga] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setPga(data.pga)).catch(() => {});
  }, []);

  if (!pga) return null;
  const inf = pga.infestation || {};
  const setInf = (patch) => setPga({ ...pga, infestation: { ...inf, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        pga: {
          auto_occurrence: !!pga.auto_occurrence,
          occurrence_level_min: pga.occurrence_level_min,
          min_photos_nc: parseInt(pga.min_photos_nc) || 1,
          infestation: {
            ...inf,
            window_days: parseInt(inf.window_days) || 60,
            per_capture_points: parseFloat(inf.per_capture_points) || 2,
            trend_multiplier: parseFloat(inf.trend_multiplier) || 1.3,
            levels: {
              medio: parseInt(inf.levels?.medio) || 25,
              alto: parseInt(inf.levels?.alto) || 50,
              critico: parseInt(inf.levels?.critico) || 75,
            },
          },
        },
      });
      toast.success("Parâmetros do índice de infestação salvos");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 border-border bg-card space-y-4" data-testid="pga-params-card">
      <div>
        <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
          <Gauge size={15} className="text-yellow-500" /> Parâmetros do Índice de Infestação (PGA)
        </h3>
        <p className="text-xs text-muted-foreground mt-1">Definem como o índice 0-100 é calculado a partir de capturas (ponderadas pelo peso da espécie) e não conformidades. Reflete imediatamente no ERP e nos apps.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Janela de análise (dias)</Label>
          <Input type="number" min="7" value={inf.window_days} onChange={(e) => setInf({ window_days: e.target.value })} data-testid="pga-param-window" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Pontos por captura</Label>
          <Input type="number" step="0.5" min="0.5" value={inf.per_capture_points} onChange={(e) => setInf({ per_capture_points: e.target.value })} data-testid="pga-param-capture-points" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Multiplicador tendência de alta</Label>
          <Input type="number" step="0.1" min="1" value={inf.trend_multiplier} onChange={(e) => setInf({ trend_multiplier: e.target.value })} data-testid="pga-param-trend" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Nível Médio a partir de</Label>
          <Input type="number" min="1" max="100" value={inf.levels?.medio} onChange={(e) => setInf({ levels: { ...inf.levels, medio: e.target.value } })} data-testid="pga-param-level-medio" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Nível Alto a partir de</Label>
          <Input type="number" min="1" max="100" value={inf.levels?.alto} onChange={(e) => setInf({ levels: { ...inf.levels, alto: e.target.value } })} data-testid="pga-param-level-alto" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Nível Crítico a partir de</Label>
          <Input type="number" min="1" max="100" value={inf.levels?.critico} onChange={(e) => setInf({ levels: { ...inf.levels, critico: e.target.value } })} data-testid="pga-param-level-critico" /></div>
      </div>
      <div className="flex flex-wrap items-center gap-5 border-t border-border pt-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={!!pga.auto_occurrence} onCheckedChange={(v) => setPga({ ...pga, auto_occurrence: v })} data-testid="pga-param-auto-occurrence" />
          Gerar ocorrência automática
        </label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">a partir do nível:</span>
          <Select value={pga.occurrence_level_min} onValueChange={(v) => setPga({ ...pga, occurrence_level_min: v })}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="pga-param-level-min"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEVEL_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Fotos mín. por NC:</span>
          <Input type="number" min="0" max="5" className="h-8 w-16" value={pga.min_photos_nc}
            onChange={(e) => setPga({ ...pga, min_photos_nc: e.target.value })} data-testid="pga-param-min-photos" />
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="ml-auto" data-testid="pga-params-save-btn">
          {saving ? "Salvando..." : "Salvar Parâmetros"}
        </Button>
      </div>
    </Card>
  );
}

export function PgaConfigPanel() {
  const { user } = useAuth();
  const [species, setSpecies] = useState([]);
  const [types, setTypes] = useState([]);
  const [speciesDialog, setSpeciesDialog] = useState(null);
  const [typeDialog, setTypeDialog] = useState(null);

  const load = useCallback(() => {
    api.get("/pga/species", { params: { include_inactive: true } }).then(({ data }) => setSpecies(data)).catch(() => {});
    api.get("/pga/structure-types", { params: { include_inactive: true } }).then(({ data }) => setTypes(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const call = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveSpecies = () => {
    if (!speciesDialog.name.trim()) return toast.error("Nome da espécie obrigatório");
    const body = { name: speciesDialog.name, scientific_name: speciesDialog.scientific_name || "",
      weight: parseFloat(speciesDialog.weight) || 1.0, requires_detail: !!speciesDialog.requires_detail };
    call(() => speciesDialog.id ? api.put(`/pga/species/${speciesDialog.id}`, body) : api.post("/pga/species", body),
      speciesDialog.id ? "Espécie atualizada" : "Espécie cadastrada");
    setSpeciesDialog(null);
  };

  const saveType = () => {
    if (!typeDialog.name.trim()) return toast.error("Nome do tipo obrigatório");
    const checklist = typeDialog.checklist.filter((c) => c.label.trim());
    if (!checklist.length) return toast.error("Inclua pelo menos um item no checklist");
    call(() => typeDialog.id ? api.put(`/pga/structure-types/${typeDialog.id}`, { name: typeDialog.name, checklist })
      : api.post("/pga/structure-types", { name: typeDialog.name, checklist }),
      typeDialog.id ? "Tipo atualizado" : "Tipo criado");
    setTypeDialog(null);
  };

  return (
    <div className="space-y-6" data-testid="pga-config-section">
      <div className="flex items-center gap-2 pt-2">
        <Wheat size={18} className="text-yellow-500" />
        <h2 className="font-heading font-bold text-base">Módulo Pragas em Grãos Armazenados (PGA)</h2>
      </div>

      <Card className="p-5 border-border bg-card space-y-4" data-testid="pga-species-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
              <Bug size={15} className="text-yellow-500" /> Espécies de Pragas de Grãos ({species.length})
            </h3>
            <p className="text-xs text-muted-foreground mt-1">Espécies disponíveis no registro de capturas do monitoramento PGA. O peso pondera o impacto no índice de infestação.</p>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => setSpeciesDialog({ name: "", scientific_name: "", weight: 1.0, requires_detail: false })} data-testid="pga-add-species-btn">
            <Plus size={13} className="mr-1" /> Adicionar Espécie
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
          {species.map((s) => (
            <div key={s.id} className={`flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card ${!s.active ? "opacity-50" : ""}`} data-testid={`pga-species-${s.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{s.name} <span className="text-[10px] text-muted-foreground">peso {s.weight}</span></p>
                {s.scientific_name && <p className="text-[10px] italic text-muted-foreground truncate">{s.scientific_name}</p>}
              </div>
              {!s.active && <Badge variant="outline" className="text-[9px] shrink-0">inativa</Badge>}
              <div className="flex items-center gap-1 shrink-0">
                <Switch checked={!!s.active} onCheckedChange={() => call(() => api.patch(`/pga/species/${s.id}/toggle`))} className="scale-[0.65]" data-testid={`pga-species-toggle-${s.id}`} />
                <button className="text-muted-foreground hover:text-primary p-0.5" onClick={() => setSpeciesDialog({ ...s })} title="Editar" data-testid={`pga-species-edit-${s.id}`}><Pencil size={13} /></button>
                {user.role === "admin" && (
                  <button className="text-muted-foreground hover:text-destructive p-0.5" title="Excluir" data-testid={`pga-species-delete-${s.id}`}
                    onClick={() => { if (window.confirm(`Excluir a espécie "${s.name}"?`)) call(() => api.delete(`/pga/species/${s.id}`), "Espécie excluída"); }}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-border bg-card space-y-4" data-testid="pga-types-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
              <Warehouse size={15} className="text-amber-400" /> Tipos de Estrutura ({types.length})
            </h3>
            <p className="text-xs text-muted-foreground mt-1">Silos, armazéns, moegas etc. — cada tipo possui seu próprio checklist de monitoramento parametrizável.</p>
          </div>
          {user.role === "admin" && (
            <Button size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => setTypeDialog({ name: "", checklist: [{ key: "", label: "", severity: "media" }] })} data-testid="pga-add-type-btn">
              <Plus size={13} className="mr-1" /> Adicionar Tipo
            </Button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
          {types.map((t) => (
            <div key={t.id} className={`flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card ${!t.active ? "opacity-50" : ""}`} data-testid={`pga-type-${t.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{t.name}</p>
                <p className="text-[10px] text-muted-foreground">{(t.checklist || []).length} item(ns) no checklist</p>
              </div>
              {!t.active && <Badge variant="outline" className="text-[9px] shrink-0">inativo</Badge>}
              {user.role === "admin" && (
                <div className="flex items-center gap-1 shrink-0">
                  <Switch checked={!!t.active} onCheckedChange={() => call(() => api.patch(`/pga/structure-types/${t.id}/toggle`))} className="scale-[0.65]" data-testid={`pga-type-toggle-${t.id}`} />
                  <button className="text-muted-foreground hover:text-primary p-0.5" onClick={() => setTypeDialog({ ...t, checklist: [...(t.checklist || [])] })} title="Editar" data-testid={`pga-type-edit-${t.id}`}><Pencil size={13} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {user.role === "admin" && <PgaParamsCard />}

      <SpeciesDialog dialog={speciesDialog} setDialog={setSpeciesDialog} onSave={saveSpecies} />
      <TypeDialog dialog={typeDialog} setDialog={setTypeDialog} onSave={saveType} />
    </div>
  );
}
